/**
 * Cable generator — a wire polyline spanning two empties, skinned at render
 * time by an auto-attached Skin modifier.
 *
 * Design notes (nonobvious, worth remembering):
 *
 *  1. World-space geometry, identity transform. The cable is semantically
 *     anchored to the two referenced endpoint objects' *world* positions,
 *     not to its own local frame. Emitting verts in world space and
 *     keeping the owning SceneObject at the origin avoids the chicken-and-
 *     egg problem of "whose transform defines the cable's space?". The
 *     `worldSpace: true` flag on `GeneratorOutput` tells the store to
 *     bypass `cursorSpawnPosition` at add-time.
 *
 *  2. Wire polyline, not a tube mesh. The produced geometry is a chain of
 *     `subdivisions + 1` verts connected by `subdivisions` `EDGE_WIRE`
 *     edges and *zero* faces. Two reasons:
 *       - The existing `generateSkinMesh()` pass turns wire edges into a
 *         parallel-transported tube at render time, giving us full Skin-
 *         modifier behaviour (radii, subdiv, fillCaps) for free.
 *       - The `settleParticles` physics solver pins every vertex that belongs
 *         to a face. A wire-only mesh has no face verts, so the whole
 *         chain is simulatable after the user triggers `simulateGravity`.
 *
 *  3. Auto-attached Skin modifier. The generator ships a `BlendSkinModifier`
 *     in its output's `generatorModifiers`. The store installs it as a
 *     `ModifierOverride { generatorOwned: true }` on the cable SceneObject
 *     and re-installs it on every rebuild. The `depth` field is kept in
 *     sync with the `thickness` param on each rebuild.
 *
 *  4. Endpoint pinning (auto). The first and last verts of the polyline
 *     have wire-edge valence 1, which `simulateGravity`'s wire-chain path
 *     already treats as anchors in object mode. So moving either empty
 *     before draping still positions the cable correctly, and the drape
 *     keeps its ends where the empties sit.
 *
 *  5. Geometry signature. The polyline depends on three inputs only:
 *     endpoint A's world position, endpoint B's world position, and
 *     `subdivisions`. Every other param — style / caps / strand count /
 *     twist / ribs / link dims / thickness / skin segments — influences
 *     only the auto-attached Skin modifier, never the underlying vertex
 *     chain. `geometrySignature()` below serializes just those three
 *     inputs so the store can preserve user-mutated vertex positions
 *     (e.g. after a gravity drape) across any appearance edit, resetting
 *     only when an endpoint moves or the subdivision count changes. A
 *     future revision could push further and re-interpolate draped verts
 *     when an endpoint moves instead of straightening the chain, but
 *     that's still out of scope.
 */

import type {
  BlendModifier,
  BlendSkinModifier,
  SkinCapStyle,
  SkinStyle,
} from "../../blend-reader/types/BlendModifier.ts";
import { MODIFIER_DEFAULTS } from "../../blend-reader/types/BlendModifier.ts";
import type { PrimitiveGeometry } from "../primitives.ts";
import {
  asLoopNormals,
  EDGE_WIRE,
  EMPTY_RENDER_TRIANGLE_INDICES,
  type PolygonTopology,
} from "../types.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorCompanion,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  defineGenerator,
  enumParam,
  intParam,
  numParam,
  objectRefParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface CableParams extends GeneratorParams {
  endA: string | null;
  endB: string | null;
  thickness: number;
  subdivisions: number;
  skinSubdivisions: number;
  style: SkinStyle;
  capStyle: SkinCapStyle;
  strandCount: number;
  twistRate: number;
  bundleJitter: number;
  ribFrequency: number;
  ribDepth: number;
  linkLength: number;
  linkAspect: number;
  wireThickness: number;
  linkOverlap: number;
  twistPerLink: number;
  linkSegments: number;
}

const CABLE_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "endA",
    label: "End A",
    type: "objectRef",
    objectKinds: ["empty"],
    default: null,
  },
  {
    id: "endB",
    label: "End B",
    type: "objectRef",
    objectKinds: ["empty"],
    default: null,
  },
  {
    id: "thickness",
    label: "Thickness",
    type: "number",
    unit: "meters",
    default: 0.02,
    min: 0.001,
    max: 1,
    step: 0.001,
  },
  {
    id: "style",
    label: "Style",
    type: "enum",
    default: "smooth",
    options: [
      { value: "smooth", label: "Smooth" },
      { value: "cable", label: "Braided Cable" },
      { value: "bundle", label: "Wire Bundle" },
      { value: "ribbed", label: "Ribbed" },
      { value: "chain", label: "Chain" },
    ],
  },
  {
    id: "capStyle",
    label: "End Caps",
    type: "enum",
    default: "flat",
    options: [
      { value: "none", label: "None" },
      { value: "flat", label: "Flat" },
      { value: "ferrule", label: "Ferrule" },
    ],
  },
  // Strand count is shared by braided-cable and wire-bundle styles (helical
  // strands for "cable", parallel jittered wires for "bundle").
  {
    id: "strandCount",
    label: "Strands",
    type: "number",
    unit: "count",
    default: 3,
    min: 2,
    max: 24,
    step: 1,
    showIf: { id: "style", value: ["cable", "bundle"] },
  },
  {
    id: "twistRate",
    label: "Twist",
    type: "number",
    unit: "count",
    default: 3,
    min: 0,
    max: 20,
    step: 0.1,
    showIf: { id: "style", value: "cable" },
  },
  {
    id: "bundleJitter",
    label: "Diameter Jitter",
    type: "number",
    default: 0.3,
    min: 0,
    max: 1,
    step: 0.01,
    showIf: { id: "style", value: "bundle" },
  },
  {
    id: "ribFrequency",
    label: "Rib Frequency",
    type: "number",
    unit: "count",
    default: 8,
    min: 1,
    max: 64,
    step: 0.5,
    showIf: { id: "style", value: "ribbed" },
  },
  {
    id: "ribDepth",
    label: "Rib Depth",
    type: "number",
    default: 0.5,
    min: 0,
    max: 1,
    step: 0.01,
    showIf: { id: "style", value: "ribbed" },
  },
  // Chain-style controls — outer link dimensions in absolute meters; chain
  // dimensions are decoupled from `thickness` so users can dial link size
  // and wire thickness independently of any tube-style fallback radius.
  // Ranges span real chain dimensions: 2 mm jewelry up to ~30 cm decorative.
  {
    id: "linkLength",
    label: "Link Length",
    type: "number",
    unit: "meters",
    default: 0.04,
    min: 0.002,
    max: 0.3,
    step: 0.001,
    showIf: { id: "style", value: "chain" },
  },
  {
    id: "linkAspect",
    label: "Link Aspect",
    type: "number",
    default: 0.6,
    min: 0.3,
    max: 1,
    step: 0.01,
    showIf: { id: "style", value: "chain" },
  },
  {
    id: "wireThickness",
    label: "Wire Thickness",
    type: "number",
    unit: "meters",
    default: 0.005,
    min: 0.0002,
    max: 0.03,
    step: 0.0001,
    showIf: { id: "style", value: "chain" },
  },
  {
    id: "linkOverlap",
    label: "Link Overlap",
    type: "number",
    default: 0.4,
    min: 0,
    max: 0.6,
    step: 0.01,
    showIf: { id: "style", value: "chain" },
  },
  {
    id: "twistPerLink",
    label: "Twist / Link",
    type: "number",
    unit: "count",
    default: 90,
    min: 0,
    max: 180,
    step: 1,
    showIf: { id: "style", value: "chain" },
  },
  {
    id: "linkSegments",
    label: "Link Segments",
    type: "number",
    unit: "count",
    default: 24,
    min: 8,
    max: 48,
    step: 1,
    showIf: { id: "style", value: "chain" },
  },
  // Mesh resolution — collapsed by default since most users won't need to adjust these.
  {
    type: "section",
    id: "cable-geometry",
    label: "Geometry",
    defaultCollapsed: true,
    params: [
      {
        id: "subdivisions",
        label: "Subdivisions",
        type: "number",
        unit: "count",
        default: 16,
        min: 1,
        max: 64,
        step: 1,
      },
      {
        id: "skinSubdivisions",
        label: "Skin Segments",
        type: "number",
        unit: "count",
        default: 8,
        min: 3,
        max: 64,
        step: 1,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry — wire polyline between two world-space endpoints
// ---------------------------------------------------------------------------

/**
 * Fallback endpoint pair when either ref is unset or resolves to null —
 * lays a 1 m cable along world +X at the origin so the user still sees
 * something sensible while wiring up refs.
 *
 * These positions also double as the default offsets for the two companion
 * empties declared below, so the fresh-add experience (cable + two empties
 * spawned and wired) visually matches the placeholder geometry the cable
 * would fall back to if either ref were cleared later.
 */
const FALLBACK_A: [number, number, number] = [-0.5, 0, 0];
const FALLBACK_B: [number, number, number] = [0.5, 0, 0];

// ---------------------------------------------------------------------------
// Companions — two empties spawned alongside the cable on add. The store
// allocates their ids, writes them into the `endA` / `endB` params, and
// creates them as ordinary sibling SceneObjects the user can select and
// drag independently. A single cable add lands a fully wired rig.
// ---------------------------------------------------------------------------

const CABLE_COMPANIONS: readonly GeneratorCompanion[] = [
  {
    id: "endA",
    objectKind: "empty",
    name: "Cable End",
    offset: FALLBACK_A,
    bindTo: "endA",
  },
  {
    id: "endB",
    objectKind: "empty",
    name: "Cable End",
    offset: FALLBACK_B,
    bindTo: "endB",
  },
];

function buildCableGeometry(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  subdivisions: number,
): PrimitiveGeometry {
  const segCount = Math.max(1, subdivisions);
  const vertCount = segCount + 1;

  const vertices = new Float32Array(vertCount * 3);
  for (let i = 0; i < vertCount; i++) {
    const t = i / segCount;
    vertices[i * 3 + 0] = a[0] + (b[0] - a[0]) * t;
    vertices[i * 3 + 1] = a[1] + (b[1] - a[1]) * t;
    vertices[i * 3 + 2] = a[2] + (b[2] - a[2]) * t;
  }

  const edges = new Uint32Array(segCount * 2);
  const edgeFlags = new Uint8Array(segCount);
  for (let i = 0; i < segCount; i++) {
    edges[i * 2 + 0] = i;
    edges[i * 2 + 1] = i + 1;
    edgeFlags[i] = EDGE_WIRE;
  }

  const topology: PolygonTopology = {
    offsets: new Uint32Array([0]), // zero faces
    corners: new Uint32Array(0),
    edges,
    cornerEdges: new Uint32Array(0),
    edgeFlags,
  };

  return {
    vertices,
    indices: EMPTY_RENDER_TRIANGLE_INDICES,
    loopNormals: asLoopNormals(new Float32Array(0)),
    topology,
    uvs: new Float32Array(0),
  };
}

/**
 * Assemble the Skin modifier the store attaches to the cable on build /
 * rebuild. `depth` tracks the `thickness` param so the rendered tube
 * follows the slider; `style` + its family of sub-params map directly
 * onto the matching `BlendSkinModifier` fields so the Skin pass
 * (`generateSkinMesh`) produces smooth / braided / ribbed visuals
 * without the generator having to know how each style is constructed.
 */
function buildCableSkinModifier(p: {
  thickness: number;
  skinSubdivisions: number;
  style: SkinStyle;
  capStyle: SkinCapStyle;
  strandCount: number;
  twistRate: number;
  bundleJitter: number;
  ribFrequency: number;
  ribDepth: number;
  linkLength: number;
  linkAspect: number;
  wireThickness: number;
  linkOverlap: number;
  twistPerLink: number;
  linkSegments: number;
}): BlendModifier {
  // `MODIFIER_DEFAULTS.skin` is typed as returning the full `BlendModifier`
  // union; narrow it to `BlendSkinModifier` before spreading so TS knows
  // `depth` / `subdivisions` are legal keys on the result.
  const base = MODIFIER_DEFAULTS.skin() as BlendSkinModifier;
  return {
    ...base,
    depth: p.thickness,
    subdivisions: p.skinSubdivisions,
    fillCaps: p.capStyle !== "none",
    capStyle: p.capStyle,
    // Bends in a draped cable are often sharp enough that the default
    // constant-radius rings visibly pinch at joints — preserveVolume
    // inflates each ring by 1/cos(halfBend) (capped at 3×) so the tube
    // reads as a physical cable instead of a flat strip.
    preserveVolume: true,
    style: p.style,
    strandCount: p.strandCount,
    twistRate: p.twistRate,
    bundleJitter: p.bundleJitter,
    ribFrequency: p.ribFrequency,
    ribDepth: p.ribDepth,
    linkLength: p.linkLength,
    linkAspect: p.linkAspect,
    wireThickness: p.wireThickness,
    linkOverlap: p.linkOverlap,
    twistPerLink: p.twistPerLink,
    linkSegments: p.linkSegments,
  };
}

/** Valid SkinStyle values — used to validate the enum-string coming back
 *  from the untyped params record. Kept in sync with the `style` spec
 *  above; a typo would silently fall back to "smooth". */
const SKIN_STYLES: readonly SkinStyle[] = [
  "smooth",
  "cable",
  "bundle",
  "ribbed",
  "chain",
];

function coerceStyle(value: string): SkinStyle {
  return (SKIN_STYLES as readonly string[]).includes(value)
    ? (value as SkinStyle)
    : "smooth";
}

const CAP_STYLES: readonly SkinCapStyle[] = ["none", "flat", "ferrule"];

function coerceCapStyle(value: string): SkinCapStyle {
  return (CAP_STYLES as readonly string[]).includes(value)
    ? (value as SkinCapStyle)
    : "flat";
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

function buildCable(
  p: CableParams,
  ctx: GeneratorBuildContext,
): GeneratorOutput {
  const endAId = objectRefParam(p, "endA");
  const endBId = objectRefParam(p, "endB");
  const thickness = numParam(p, "thickness", 0.02);
  const subdivisions = intParam(p, "subdivisions", 16);
  const skinSubdivisions = intParam(p, "skinSubdivisions", 8);
  const style = coerceStyle(enumParam(p, "style", "smooth"));
  const strandCount = intParam(p, "strandCount", 3);
  const twistRate = numParam(p, "twistRate", 3);
  const bundleJitter = numParam(p, "bundleJitter", 0.3);
  const ribFrequency = numParam(p, "ribFrequency", 8);
  const ribDepth = numParam(p, "ribDepth", 0.5);
  const linkLength = numParam(p, "linkLength", 0.04);
  const linkAspect = numParam(p, "linkAspect", 0.6);
  const wireThickness = numParam(p, "wireThickness", 0.005);
  const linkOverlap = numParam(p, "linkOverlap", 0.4);
  const twistPerLink = numParam(p, "twistPerLink", 90);
  const linkSegments = intParam(p, "linkSegments", 24);

  const capStyle = coerceCapStyle(enumParam(p, "capStyle", "flat"));

  const endA = ctx.resolveObject(endAId);
  const endB = ctx.resolveObject(endBId);

  const posA: readonly [number, number, number] = endA
    ? [endA.position[0], endA.position[1], endA.position[2]]
    : FALLBACK_A;
  const posB: readonly [number, number, number] = endB
    ? [endB.position[0], endB.position[1], endB.position[2]]
    : FALLBACK_B;

  const geometry = buildCableGeometry(posA, posB, subdivisions);
  const skin = buildCableSkinModifier({
    thickness,
    skinSubdivisions,
    style,
    capStyle,
    strandCount,
    twistRate,
    bundleJitter,
    ribFrequency,
    ribDepth,
    linkLength,
    linkAspect,
    wireThickness,
    linkOverlap,
    twistPerLink,
    linkSegments,
  });

  return {
    kind: "mesh",
    geometry,
    worldSpace: true,
    generatorModifiers: [skin],
  };
}

/**
 * Identity token over the inputs that influence polyline geometry. Style
 * params are intentionally excluded — they live on the Skin modifier, so
 * leaving them out of the signature lets the store skip the geometry
 * write on a style-only edit and preserve a draped chain's vertices.
 *
 * Endpoint positions enter via `resolveObject` so companion-empty drags
 * invalidate the signature automatically (the endpoint-moved path in the
 * store feeds the same params into a rebuild — the signature flips when
 * the empty's world position changes, triggering a fresh polyline).
 *
 * Position components are rounded to 1e-6 m before formatting so
 * sub-micrometre float drift doesn't spuriously invalidate the
 * signature; real user movements sit far above that threshold.
 */
function cableGeometrySignature(
  p: CableParams,
  ctx: GeneratorBuildContext,
): string {
  const endA = ctx.resolveObject(objectRefParam(p, "endA"));
  const endB = ctx.resolveObject(objectRefParam(p, "endB"));
  const posA = endA ? endA.position : FALLBACK_A;
  const posB = endB ? endB.position : FALLBACK_B;
  const subdivisions = intParam(p, "subdivisions", 16);
  const q = (n: number) => Math.round(n * 1e6) / 1e6;
  return [
    q(posA[0]),
    q(posA[1]),
    q(posA[2]),
    q(posB[0]),
    q(posB[1]),
    q(posB[2]),
    subdivisions,
  ].join("|");
}

export const CABLE_GENERATOR: Generator = defineGenerator<CableParams>({
  id: "cable",
  label: "Cable",
  category: "prop",
  paramSpecs: CABLE_SPECS,
  defaultParams: paramsFromSpecs(CABLE_SPECS) as CableParams,
  companions: CABLE_COMPANIONS,
  // Surface the gravity solver on the cable's sidebar section so the
  // drape workflow is one click from the place the user is already
  // adjusting thickness / subdivisions. The operator reads the current
  // selection to pick its target, which is always the cable itself
  // while the panel is open (see GeneratorPanel's panel-scoped
  // execution wrapper).
  operatorIds: ["simulate_gravity"],
  build: buildCable,
  geometrySignature: cableGeometrySignature,
});
